/**
 * URL Helper
 *
 * Generates URLs for forms and proposals, supporting custom domains per organization.
 * If an organization has a custom_domain set, it will be used instead of the default env URLs.
 *
 * Proposal URLs use localized routes:
 * - Dutch (nl-NL): /offerte/[id]
 * - English: /proposal/[id]
 */

/**
 * Get the base URL for forms/proposals
 * @param {Object} organization - The organization object
 * @returns {string} The base URL
 */
export function getBaseUrl(organization) {
  if (organization?.custom_domain) {
    return `https://${organization.custom_domain}`;
  }
  return import.meta.env.VITE_FORMS_URL;
}

/**
 * Get the full URL for a specific form
 * @param {Object} organization - The organization object
 * @param {string} formId - The form ID
 * @returns {string} The full form URL
 */
export function getFormUrl(organization, formId) {
  const baseUrl = getBaseUrl(organization);
  return `${baseUrl}/form/${formId}`;
}

/**
 * Get the full URL for a specific proposal
 * Uses /offerte/ for Dutch organizations, /proposal/ for others
 * @param {Object} organization - The organization object
 * @param {string} proposalId - The proposal ID
 * @returns {string} The full proposal URL
 */
export function getProposalUrl(organization, proposalId) {
  const baseUrl = getBaseUrl(organization);
  const route = organization?.language === 'nl-NL' ? 'offerte' : 'proposal';
  return `${baseUrl}/${route}/${proposalId}`;
}

/**
 * Get the full public URL for a specific invoice (token-based).
 * Served by the forms app at /i/[token].
 * @param {Object} organization - The organization object
 * @param {string} token - The invoice public token
 * @returns {string} The full public invoice URL
 */
export function getInvoiceUrl(organization, token) {
  const baseUrl = getBaseUrl(organization);
  return `${baseUrl}/i/${token}`;
}
